import { htmlLanguage } from "@codemirror/lang-html";

export interface PreviewDocumentOptions {
    baseUrl?: string | null;
    instrumentScript?: string | null;
}

const INERT_OR_RAW_ELEMENTS = new Set([
    "template", "svg", "math", "script", "style", "textarea", "title",
    "xmp", "iframe", "noembed", "noframes", "noscript", "plaintext"
]);

function escapeAttribute(value: string): string {
    return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

export function buildPreviewDocument(
    userCode: string,
    options: PreviewDocumentOptions = {}
): string {
    const code = typeof userCode === "string" ? userCode : "";
    const tree = htmlLanguage.parser.parse(code);
    let headEnd: number | null = null;
    let htmlEnd: number | null = null;
    let doctypeEnd: number | null = null;
    let baseTag: { from: number; to: number } | null = null;

    tree.iterate({
        enter(node) {
            if (node.name === "DoctypeDecl") doctypeEnd ??= node.to;
            if (node.name !== "Element") return;
            const openTag = node.node.getChild("OpenTag") || node.node.getChild("SelfClosingTag");
            const nameNode = openTag?.getChild("TagName");
            if (!openTag || !nameNode) return;
            const name = code.slice(nameNode.from, nameNode.to).toLowerCase();
            if (INERT_OR_RAW_ELEMENTS.has(name)) return false;

            const parent = node.node.parent;
            const parentTag = parent?.getChild("OpenTag")?.getChild("TagName");
            const parentName = parentTag ? code.slice(parentTag.from, parentTag.to).toLowerCase() : "";
            if (name === "html" && parent?.name === "Document") htmlEnd ??= openTag.to;
            if (name === "head" && (parent?.name === "Document" || parentName === "html")) headEnd ??= openTag.to;
            if (name === "base" && !baseTag) {
                const hasHref = openTag.getChildren("Attribute").some(attribute => {
                    const attributeName = attribute.getChild("AttributeName");
                    return attributeName && code.slice(attributeName.from, attributeName.to).toLowerCase() === "href";
                });
                if (hasHref) baseTag = { from: openTag.from, to: openTag.to };
            }
        }
    });

    const edits: { from: number; to: number; content: string }[] = [];
    const parts: string[] = [];
    if (options.baseUrl) {
        let baseUrl = document.baseURI;
        try {
            baseUrl = new URL(options.baseUrl, document.baseURI).href;
        } catch {
            baseUrl = document.baseURI;
        }
        if (baseTag) {
            const { from, to } = baseTag;
            const template = document.createElement("template");
            template.innerHTML = code.slice(from, to);
            const base = template.content.querySelector("base");
            if (base) {
                try {
                    base.setAttribute("href", new URL(base.getAttribute("href") || "", baseUrl).href);
                } catch {
                    base.setAttribute("href", baseUrl);
                }
                edits.push({ from, to, content: base.outerHTML });
            }
        } else {
            parts.push(`<base href="${escapeAttribute(baseUrl)}">`);
        }
    }
    if (options.instrumentScript) parts.push(options.instrumentScript);
    const headContent = parts.join("\n");
    const isDocument = doctypeEnd !== null || htmlEnd !== null || headEnd !== null;

    if (isDocument && headContent) {
        const insertAt = headEnd ?? htmlEnd ?? doctypeEnd ?? 0;
        const content = headEnd !== null ? `\n${headContent}\n` : `\n<head>\n${headContent}\n</head>\n`;
        edits.push({ from: insertAt, to: insertAt, content });
    }

    let result = code;
    edits.sort((left, right) => right.from - left.from || right.to - left.to);
    for (const edit of edits) result = result.slice(0, edit.from) + edit.content + result.slice(edit.to);
    if (isDocument) return result;
    return `<!DOCTYPE html>\n<html>\n<head>\n<meta charset="utf-8">\n${headContent}\n</head>\n<body>\n${result}\n</body>\n</html>`;
}
